import { topAttractionsSlides } from "@/data/top-attractions";
import { recommendationWinter, recommendationSummer } from "@/data/recommendation-sections";
import AttractionDetailLayout from "@/components/sections/AttractionDetailLayout";
import RootNotFound from "@/app/not-found";
import { Helmet } from "react-helmet-async";
import { SITE_URL, normalizeLocale, sitePageUrl, ogLocaleTag } from "@/lib/site";

const DEFAULT_EXPERIENCE_DESCRIPTION =
  "Discover one of the highlights along the Grand Tour of Switzerland: a mix of scenic routes, authentic villages and unforgettable viewpoints – perfect for your next Swiss getaway.";

function getAllExperiences() {
  const recItems = [...recommendationWinter, ...recommendationSummer].map((item) => ({
    id: item.id,
    title: item.label,
    description: DEFAULT_EXPERIENCE_DESCRIPTION,
    imageSrc: item.imageSrc,
    imageAlt: item.imageAlt,
    category: "Experience",
  }));

  const attractionItems = topAttractionsSlides.map((item) => ({
    id: item.id,
    title: item.title,
    description: item.description,
    imageSrc: item.imageSrc,
    imageAlt: item.imageAlt,
    category: item.category || "Top attraction",
  }));

  return [...recItems, ...attractionItems];
}

export default function AttractionDetailPage({ params }) {
  const { id } = params;
  const locale = normalizeLocale(params?.locale);

  const allExperiences = getAllExperiences();
  const experience = allExperiences.find((item) => item.id === id);

  if (!experience) {
    return <RootNotFound />;
  }

  const pageUrl = sitePageUrl(locale, "experiences", id);
  const homeUrl = sitePageUrl(locale);
  const metaTitle = `${experience.title} | Highlandgroup`;
  const metaDescription = experience.description;
  const imageUrl = experience.imageSrc?.startsWith("http")
    ? experience.imageSrc
    : `${SITE_URL}${experience.imageSrc}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: metaTitle,
        description: metaDescription,
        inLanguage: locale,
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: imageUrl,
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: homeUrl },
          {
            "@type": "ListItem",
            position: 2,
            name: experience.title,
            item: pageUrl,
          },
        ],
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="Switzerland, Highlandgroup, experiences, travel, digital"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={experience.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:locale" content={ogLocaleTag(locale)} />
        <meta property="og:site_name" content="Highlandgroup" />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={experience.title} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <main className="bg-bg-main text-charcoal min-h-screen">
        <AttractionDetailLayout
          title={experience.title}
          description={experience.description}
          imageSrc={experience.imageSrc}
          imageAlt={experience.imageAlt}
          category={experience.category}
        />
      </main>
    </>
  );
}
